import { useCallback, useState } from "react";
import { toast } from "sonner";
import { createLead, uploadDocument, notifyLead } from "@/lib/apiClient";

export const MAX_CHARS = 500;
const EMPTY_FORM = { name: "", phone: "", email: "", message: "" };
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateForm = (form, msgs) => {
  const e = {};
  if (!form.name.trim()) e.name = msgs.errName;
  if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7) e.phone = msgs.errPhone;
  if (!EMAIL_RX.test(form.email)) e.email = msgs.errEmail;
  if (!form.message.trim()) e.message = msgs.errMessage;
  return e;
};

const uploadAll = async (files, leadId) => {
  let failed = 0;
  for (const f of files) {
    try { await uploadDocument(f, leadId); }
    catch (err) { console.error("Document upload failed:", err); failed += 1; }
  }
  return failed;
};

/**
 * Business logic for the contact / document portal: form state, validation,
 * file list management and the submit pipeline (lead -> uploads -> notify).
 */
export const useLeadSubmission = ({ t, lang }) => {
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const addFiles = useCallback((accepted) => {
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => f.name + f.size));
      const merged = [...prev];
      accepted.forEach((f) => { if (!seen.has(f.name + f.size)) merged.push(f); });
      return merged;
    });
  }, []);

  const removeFile = useCallback((idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const setField = useCallback((k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  }, []);

  const reset = useCallback(() => {
    setForm(EMPTY_FORM);
    setFiles([]);
    setErrors({});
    setSuccess(false);
  }, []);

  const submit = useCallback(async () => {
    const e = validateForm(form, t.portal);
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setSubmitting(true);
    try {
      const lead = await createLead({ ...form, language: lang });
      const failed = await uploadAll(files, lead.id);
      if (failed > 0) toast.warning(`${t.portal.uploadError}: ${failed}`);
      // Email notifications must never block the success state.
      try { await notifyLead(lead.id); }
      catch (notifyErr) { console.error("Lead notification failed (non-blocking):", notifyErr); }
      setSuccess(true);
      toast.success(t.portal.successTitle);
    } catch (err) {
      console.error("Lead submission failed:", err);
      toast.error(t.portal.leadError);
    } finally {
      setSubmitting(false);
    }
  }, [form, files, lang, t]);

  return {
    files, form, errors, submitting, success,
    addFiles, removeFile, setField, submit, reset,
  };
};
